import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Menu, X, ArrowRight, Code, Smartphone, Palette, Zap, 
  Users, Target, Rocket, ChevronDown, ChevronUp, 
  Mail, MapPin, Clock, ExternalLink, Check, Layers,
  Search, Heart, Home, Building, CreditCard, FileText, Bell,
  Calendar, PartyPopper, Shield, Megaphone
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#portfolio", label: "Work" },
    { href: "#case-studies", label: "Case Studies" },
    { href: "#process", label: "Process" },
    { href: "#team", label: "Team" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="text-2xl font-medium tracking-tight" data-testid="logo">
            <span className="text-white">Mindful</span>
            <span className="text-neutral-400">UX</span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-sm text-neutral-400 hover:text-white transition-colors duration-200"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact">
              <Button 
                className="bg-white text-black hover:bg-neutral-200 rounded-full px-6 btn-shine"
                data-testid="nav-cta-button"
              >
                Get Started
              </Button>
            </a>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/5">
          <div className="container-custom py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-neutral-400 hover:text-white py-2 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
              <Button className="bg-white text-black hover:bg-neutral-200 rounded-full w-full mt-4">
                Get Started
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="hero-bg grid-pattern min-h-screen flex items-center relative overflow-hidden"
      style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1721549369164-2fd07f795ce4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGJsYWNrJTIwZ2VvbWV0cmljJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmQlMjBkYXJrJTIwbGluZWFyJTIwZ3JhZGllbnR8ZW58MHx8fHwxNzczNTE2NjQ4fDA&ixlib=rb-4.1.0&q=85)`,
      }}
    >
      <div className="container-custom relative z-10 pt-32 pb-24">
        <div className="max-w-4xl">
          <p className="text-sm text-neutral-500 uppercase tracking-widest mb-6 animate-fade-in">
            Digital Experience Studio
          </p>
          <h1 className="text-6xl md:text-8xl font-medium tracking-tight leading-none mb-8 animate-fade-in stagger-1">
            Crafting
            <br />
            <span className="text-gradient">Digital</span>
            <br />
            Experiences
          </h1>
          <p className="text-lg md:text-xl text-neutral-400 leading-relaxed max-w-2xl mb-12 animate-fade-in stagger-2">
            We design and build web and mobile applications with obsessive 
            attention to user experience. Every pixel, every interaction, 
            every moment—thoughtfully crafted.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
            <a href="#portfolio">
              <Button 
                className="bg-white text-black hover:bg-neutral-200 px-8 py-6 rounded-full font-medium text-base btn-shine"
                data-testid="hero-view-work-btn"
              >
                View Our Work
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </a>
            <a href="#contact">
              <Button 
                variant="outline"
                className="border-neutral-800 text-white hover:border-white hover:bg-neutral-900 px-8 py-6 rounded-full font-medium text-base"
                data-testid="hero-contact-btn"
              >
                Book a Consultation
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-float">
        <a href="#about" className="text-neutral-500 hover:text-white transition-colors">
          <ChevronDown size={32} />
        </a>
      </div>
    </section>
  );
};

const About = () => {
  const stats = [
    { value: "50+", label: "Projects Delivered" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "5+", label: "Years Experience" },
    { value: "24/7", label: "Support" },
  ];

  return (
    <section id="about" data-testid="about-section" className="section-gap bg-[#050505]">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
              About Us
            </p>
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight mb-8">
              We believe in
              <br />
              <span className="text-neutral-400">mindful design</span>
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-6">
              MindfulUX is a digital studio specializing in creating exceptional 
              web and mobile applications. We don't just build products—we craft 
              experiences that resonate with users on a deeper level.
            </p>
            <p className="text-neutral-400 leading-relaxed mb-8">
              Our approach combines strategic thinking, meticulous attention to 
              detail, and cutting-edge technology to deliver solutions that not 
              only look beautiful but perform flawlessly.
            </p>
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="border-l border-neutral-800 pl-4">
                  <p className="text-3xl md:text-4xl font-medium text-white mb-1">
                    {stat.value}
                  </p>
                  <p className="text-sm text-neutral-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden border border-neutral-800 img-zoom">
              <img
                src="https://images.unsplash.com/photo-1527245240659-ccf507abd66c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsYWNrJTIwZ2VvbWV0cmljJTIwdGVjaG5vbG9neSUyMGJhY2tncm91bmQlMjBkYXJrJTIwbGluZWFyJTIwZ3JhZGllbnR8ZW58MHx8fHwxNzczNTE2NjQ4fDA&ixlib=rb-4.1.0&q=85"
                alt="Abstract design"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-neutral-900 border border-neutral-800 rounded-xl p-6 animate-pulse-glow">
              <p className="text-2xl font-medium">100%</p>
              <p className="text-sm text-neutral-500">Commitment to Quality</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    {
      icon: <Code strokeWidth={1.5} size={32} />,
      title: "Web Development",
      description: "Custom web applications built with modern frameworks, optimized for performance and scalability.",
    },
    {
      icon: <Smartphone strokeWidth={1.5} size={32} />,
      title: "Mobile Apps",
      description: "Native and cross-platform mobile applications that deliver seamless experiences across devices.",
    },
    {
      icon: <Palette strokeWidth={1.5} size={32} />,
      title: "UI/UX Design",
      description: "User-centered design that combines aesthetics with functionality for intuitive interfaces.",
    },
    {
      icon: <Zap strokeWidth={1.5} size={32} />,
      title: "Performance",
      description: "Optimization and fine-tuning to ensure your applications run at peak efficiency.",
    },
  ];

  return (
    <section id="services" data-testid="services-section" className="section-gap bg-[#0a0a0a]">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
            Services
          </p>
          <h2 className="text-4xl md:text-6xl font-medium tracking-tight">
            What we do
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-8 card-hover group"
              data-testid={`service-card-${index}`}
            >
              <div className="text-white mb-6 group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              <h3 className="text-2xl font-medium mb-4">{service.title}</h3>
              <p className="text-neutral-400 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Portfolio = () => {
  const projects = [
    {
      title: "u-dorm",
      category: "Web Platform",
      description: "The #1 Dormitory Management Platform. An all-in-one solution for landlords, tenants, and property managers—from tenant onboarding to financial reporting.",
      image: "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/l4dit5fr_screen.jpg",
      large: true,
      features: ["Multi-Property Management", "Payment Tracking", "Digital Leases"],
    },
    {
      title: "batchm8s",
      category: "Web Platform",
      description: "Reunion Made Easy. A platform that helps you organize school reunions effortlessly—manage events, track payments, and reconnect with classmates all in one place.",
      image: "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/jbg8ai70_screen.jpg",
      large: false,
      features: ["Member Directory", "Event Program", "Payment Tracking"],
    },
  ];

  return (
    <section id="portfolio" data-testid="portfolio-section" className="section-gap bg-[#050505]">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
              Portfolio
            </p>
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight">
              Featured Work
            </h2>
          </div>
          <p className="text-neutral-400 mt-4 md:mt-0 max-w-md">
            Explore our latest projects where thoughtful UX meets cutting-edge technology.
          </p>
        </div>

        <div className="bento-grid">
          {projects.map((project, index) => (
            <div
              key={index}
              className={`${project.large ? "bento-large" : ""} group relative overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900/50 card-hover`}
              data-testid={`portfolio-card-${project.title.toLowerCase()}`}
            >
              <div className="aspect-video img-zoom">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs text-neutral-500 uppercase tracking-widest">
                    {project.category}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-medium mb-4 group-hover:text-gradient transition-all">
                  {project.title}
                </h3>
                <p className="text-neutral-400 leading-relaxed mb-6">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.features.map((feature, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 text-xs bg-neutral-800/50 border border-neutral-700 rounded-full text-neutral-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);

  const caseStudies = [
    {
      id: "udorm",
      title: "u-dorm",
      tagline: "Simplify Your Dormitory Management",
      category: "Web Platform",
      duration: "4 months",
      year: "2024",
      heroImage: "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/l4dit5fr_screen.jpg",
      galleryImages: [
        "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/b88fa78w_screen1.jpg",
        "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/pjdejba2_screen2.jpg"
      ],
      challenge: "Managing dormitories and boarding houses was chaotic—landlords struggled with tenant onboarding, payment tracking, and lease management across multiple properties. Tenants had no easy way to find verified housing or communicate with landlords.",
      solution: "We built an all-in-one platform that serves both landlords and tenants. Landlords get multi-property management, automated payment reminders, and digital lease agreements. Tenants enjoy easy room search, secure transactions, and direct communication with landlords.",
      outcomes: [
        { metric: "100+", label: "Properties managed" },
        { metric: "500+", label: "Active tenants" },
        { metric: "98%", label: "Success rate" },
        { metric: "₱1.2M", label: "Revenue tracked" },
      ],
      features: [
        { icon: <Building strokeWidth={1.5} size={20} />, title: "Multi-Property Management", desc: "Manage unlimited properties and units from a single dashboard" },
        { icon: <CreditCard strokeWidth={1.5} size={20} />, title: "Payment Tracking", desc: "Track rent payments, verify proofs, and generate receipts automatically" },
        { icon: <FileText strokeWidth={1.5} size={20} />, title: "Digital Lease Agreements", desc: "Create, sign, and manage lease contracts digitally with PDF export" },
        { icon: <Bell strokeWidth={1.5} size={20} />, title: "Automated Reminders", desc: "Send payment reminders and late notices automatically via email" },
      ],
      process: [
        { phase: "Research", desc: "Interviewed 50+ landlords and 200+ tenants to understand pain points" },
        { phase: "Design", desc: "Created dual-persona interfaces for landlords and tenants" },
        { phase: "Testing", desc: "Beta tested with 20 properties and 100+ tenants" },
        { phase: "Launch", desc: "Phased rollout with continuous feedback loops" },
      ],
      techStack: ["Next.js", "Node.js", "PostgreSQL", "AWS", "Stripe"],
    },
    {
      id: "batchm8s",
      title: "batchm8s",
      tagline: "Reconnect with Your Batchmates",
      category: "Web Platform",
      duration: "3 months",
      year: "2025",
      heroImage: "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/jbg8ai70_screen.jpg",
      galleryImages: [
        "https://customer-assets.emergentagent.com/job_zen-interface-5/artifacts/8xj8kivn_screen1.jpg"
      ],
      challenge: "Organizing school reunions was a nightmare—scattered communication, manual payment tracking, and no central place for event details. Alumni lost touch with classmates and reunion organizers spent countless hours on logistics.",
      solution: "We created a reunion management platform that makes organizing effortless. From member directories to event programs, payment tracking to announcements—everything alumni need to reconnect and celebrate together.",
      outcomes: [
        { metric: "500+", label: "Alumni registered" },
        { metric: "25+", label: "Batch years" },
        { metric: "10+", label: "Reunions organized" },
        { metric: "150+", label: "Confirmed attendees" },
      ],
      features: [
        { icon: <Users strokeWidth={1.5} size={20} />, title: "Member Directory", desc: "Reconnect with batchmates, search by batch year and find old friends" },
        { icon: <Megaphone strokeWidth={1.5} size={20} />, title: "Announcements", desc: "Stay updated with the latest news and never miss important updates" },
        { icon: <Calendar strokeWidth={1.5} size={20} />, title: "Event Program", desc: "View complete sequence of events and RSVP to activities" },
        { icon: <Shield strokeWidth={1.5} size={20} />, title: "Secure Payments", desc: "Pay via GCash or bank transfer, upload proof and track payments" },
      ],
      process: [
        { phase: "Discovery", desc: "Interviewed reunion organizers and analyzed pain points" },
        { phase: "Prototyping", desc: "Built interactive prototypes for organizer and attendee flows" },
        { phase: "Iteration", desc: "5 design sprints based on organizer feedback" },
        { phase: "Launch", desc: "Launched for Grand Reunion 2025 with 150+ attendees" },
      ],
      techStack: ["Next.js", "Firebase", "Cloud Functions", "GCash API", "Vercel"],
    },
  ];

  return (
    <section id="case-studies" data-testid="case-studies-section" className="section-gap bg-[#0a0a0a]">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
            Case Studies
          </p>
          <h2 className="text-4xl md:text-6xl font-medium tracking-tight mb-6">
            Deep Dive into
            <br />
            <span className="text-neutral-400">Our Process</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Explore how we approach complex problems and transform ideas into 
            exceptional digital products.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {caseStudies.map((study) => (
            <div
              key={study.id}
              className="group relative bg-neutral-900/50 border border-neutral-800 rounded-2xl overflow-hidden card-hover cursor-pointer"
              onClick={() => setSelectedCase(study)}
              data-testid={`case-study-card-${study.id}`}
            >
              <div className="aspect-video relative overflow-hidden">
                <img
                  src={study.heroImage}
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-neutral-400 uppercase tracking-widest">
                      {study.category}
                    </span>
                    <span className="text-xs text-neutral-600">•</span>
                    <span className="text-xs text-neutral-400">{study.year}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-medium">{study.title}</h3>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-lg text-neutral-300 mb-4">{study.tagline}</p>
                <p className="text-neutral-500 text-sm leading-relaxed mb-6 line-clamp-2">
                  {study.challenge}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex gap-6">
                    {study.outcomes.slice(0, 2).map((outcome, i) => (
                      <div key={i}>
                        <p className="text-xl font-medium text-white">{outcome.metric}</p>
                        <p className="text-xs text-neutral-500">{outcome.label}</p>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    className="text-neutral-400 hover:text-white group-hover:translate-x-1 transition-transform"
                    data-testid={`view-case-study-${study.id}`}
                  >
                    View Case Study
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Case Study Modal */}
      <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border-neutral-800 p-0 [&>button]:z-50 [&>button]:bg-black/50 [&>button]:backdrop-blur-sm [&>button]:rounded-full [&>button]:p-2 [&>button]:border [&>button]:border-white/20 [&>button]:hover:bg-black/80 [&>button]:top-4 [&>button]:right-4">
          <VisuallyHidden>
            <DialogTitle>{selectedCase?.title} Case Study</DialogTitle>
          </VisuallyHidden>
          {selectedCase && (
            <div data-testid={`case-study-modal-${selectedCase.id}`}>
              {/* Hero */}
              <div className="relative h-64 md:h-80">
                <img
                  src={selectedCase.heroImage}
                  alt={selectedCase.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/50 to-transparent" />
                <div className="absolute bottom-6 left-8 right-8">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-3 py-1 text-xs bg-white/10 rounded-full text-neutral-300">
                      {selectedCase.category}
                    </span>
                    <span className="text-xs text-neutral-400">{selectedCase.duration}</span>
                    <span className="text-xs text-neutral-600">•</span>
                    <span className="text-xs text-neutral-400">{selectedCase.year}</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-medium">{selectedCase.title}</h2>
                  <p className="text-xl text-neutral-400 mt-2">{selectedCase.tagline}</p>
                </div>
              </div>

              <div className="p-8 space-y-12">
                {/* Outcomes Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {selectedCase.outcomes.map((outcome, i) => (
                    <div key={i} className="text-center p-4 bg-neutral-900/50 rounded-xl border border-neutral-800">
                      <p className="text-3xl font-medium text-white mb-1">{outcome.metric}</p>
                      <p className="text-sm text-neutral-500">{outcome.label}</p>
                    </div>
                  ))}
                </div>

                {/* Challenge */}
                <div>
                  <h3 className="text-sm text-neutral-500 uppercase tracking-widest mb-4">The Challenge</h3>
                  <p className="text-lg text-neutral-300 leading-relaxed">{selectedCase.challenge}</p>
                </div>

                {/* Solution */}
                <div>
                  <h3 className="text-sm text-neutral-500 uppercase tracking-widest mb-4">Our Solution</h3>
                  <p className="text-lg text-neutral-300 leading-relaxed">{selectedCase.solution}</p>
                </div>

                {/* Features */}
                <div>
                  <h3 className="text-sm text-neutral-500 uppercase tracking-widest mb-6">Key Features</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {selectedCase.features.map((feature, i) => (
                      <div key={i} className="flex gap-4 p-4 bg-neutral-900/30 rounded-xl border border-neutral-800/50">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neutral-800 flex items-center justify-center text-neutral-400">
                          {feature.icon}
                        </div>
                        <div>
                          <h4 className="font-medium mb-1">{feature.title}</h4>
                          <p className="text-sm text-neutral-500">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Process */}
                <div>
                  <h3 className="text-sm text-neutral-500 uppercase tracking-widest mb-6">Our Process</h3>
                  <div className="space-y-4">
                    {selectedCase.process.map((step, i) => (
                      <div key={i} className="flex gap-4 items-start">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white text-black flex items-center justify-center text-sm font-medium">
                          {i + 1}
                        </div>
                        <div className="pt-1">
                          <h4 className="font-medium mb-1">{step.phase}</h4>
                          <p className="text-sm text-neutral-500">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tech Stack */}
                <div>
                  <h3 className="text-sm text-neutral-500 uppercase tracking-widest mb-4">Tech Stack</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCase.techStack.map((tech, i) => (
                      <span
                        key={i}
                        className="px-4 py-2 text-sm bg-neutral-800/50 border border-neutral-700 rounded-full text-neutral-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="pt-6 border-t border-neutral-800">
                  <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <p className="text-neutral-400">Interested in a similar project?</p>
                    <a href="#contact" onClick={() => setSelectedCase(null)}>
                      <Button className="bg-white text-black hover:bg-neutral-200 rounded-full px-6">
                        Let's Talk
                        <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

const Team = () => {
  const team = [
    {
      name: "Alex Rivera",
      role: "Founder & Lead Designer",
      image: "https://images.unsplash.com/photo-1767821853493-cf1aacf60010?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGJsYWNrJTIwYmFja2dyb3VuZCUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzUxNjY0OXww&ixlib=rb-4.1.0&q=85",
    },
    {
      name: "Jordan Kim",
      role: "Technical Lead",
      image: "https://images.unsplash.com/photo-1763757321162-95c0de309d22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGJsYWNrJTIwYmFja2dyb3VuZCUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzUxNjY0OXww&ixlib=rb-4.1.0&q=85",
    },
    {
      name: "Casey Morgan",
      role: "UX Researcher",
      image: "https://images.unsplash.com/photo-1761429943391-a7cf7db5ac83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGJsYWNrJTIwYmFja2dyb3VuZCUyMHN0dWRpbyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc3MzUxNjY0OXww&ixlib=rb-4.1.0&q=85",
    },
  ];

  return (
    <section id="team" data-testid="team-section" className="section-gap bg-[#050505]">
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
            Team
          </p>
          <h2 className="text-4xl md:text-6xl font-medium tracking-tight">
            Meet the Minds
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {team.map((member, index) => (
            <div
              key={index}
              className="group text-center"
              data-testid={`team-member-${index}`}
            >
              <div className="relative mb-6 rounded-2xl overflow-hidden border border-neutral-800 img-zoom">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full aspect-[3/4] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <h3 className="text-xl font-medium mb-2">{member.name}</h3>
              <p className="text-neutral-500">{member.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const steps = [
    {
      number: "01",
      title: "Discovery",
      description: "We dive deep into understanding your vision, goals, and target audience through collaborative workshops.",
      icon: <Target strokeWidth={1.5} size={24} />,
    },
    {
      number: "02",
      title: "Strategy",
      description: "Based on our findings, we develop a comprehensive strategy that aligns with your business objectives.",
      icon: <Users strokeWidth={1.5} size={24} />,
    },
    {
      number: "03",
      title: "Design",
      description: "Our designers craft intuitive, beautiful interfaces that resonate with your users.",
      icon: <Palette strokeWidth={1.5} size={24} />,
    },
    {
      number: "04",
      title: "Development",
      description: "We build robust, scalable applications using cutting-edge technologies and best practices.",
      icon: <Code strokeWidth={1.5} size={24} />,
    },
    {
      number: "05",
      title: "Launch",
      description: "Rigorous testing ensures a flawless launch. We stay with you for ongoing support and optimization.",
      icon: <Rocket strokeWidth={1.5} size={24} />,
    },
  ];

  return (
    <section id="process" data-testid="process-section" className="section-gap bg-[#0a0a0a]">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
              Process
            </p>
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight mb-8">
              How we work
            </h2>
            <p className="text-neutral-400 leading-relaxed">
              Our proven process ensures every project is delivered with 
              excellence. From initial concept to final launch, we maintain 
              transparency and collaboration at every step.
            </p>
          </div>

          <div className="relative">
            <div className="timeline-line hidden md:block" />
            <div className="space-y-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="flex gap-6 group"
                  data-testid={`process-step-${index}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-neutral-400 group-hover:border-white group-hover:text-white transition-all duration-300">
                    {step.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-neutral-600 font-mono">
                        {step.number}
                      </span>
                      <h3 className="text-xl font-medium">{step.title}</h3>
                    </div>
                    <p className="text-neutral-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "What is your typical project timeline?",
      answer: "Project timelines vary based on scope and complexity. A typical web application takes 8-12 weeks, while mobile apps may take 12-16 weeks. We'll provide a detailed timeline during our initial consultation.",
    },
    {
      question: "Do you offer ongoing support after launch?",
      answer: "Yes! We offer various support packages including bug fixes, feature updates, performance optimization, and 24/7 monitoring. We believe in long-term partnerships with our clients.",
    },
    {
      question: "What technologies do you work with?",
      answer: "We specialize in modern web and mobile technologies including React, React Native, Node.js, Python, and cloud platforms like AWS and GCP. We choose the best tech stack based on your specific needs.",
    },
    {
      question: "How do you handle project communication?",
      answer: "We use a combination of weekly video calls, a dedicated Slack channel, and project management tools like Linear or Notion. You'll always know the status of your project.",
    },
    {
      question: "What is your pricing model?",
      answer: "We offer both fixed-price projects and retainer arrangements. After understanding your requirements, we'll provide a detailed proposal with transparent pricing and no hidden costs.",
    },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="section-gap bg-[#050505]">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
              FAQ
            </p>
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight">
              Questions?
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-neutral-800 rounded-xl px-6 bg-neutral-900/30 data-[state=open]:border-neutral-700"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-lg font-medium py-6 hover:no-underline [&[data-state=open]>svg]:rotate-180">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-400 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-gap bg-[#0a0a0a]">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-sm text-neutral-500 uppercase tracking-widest mb-4">
              Contact
            </p>
            <h2 className="text-4xl md:text-6xl font-medium tracking-tight mb-8">
              Let's create
              <br />
              something great
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-12">
              Ready to transform your ideas into reality? Get in touch and let's 
              discuss how we can help you create exceptional digital experiences.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <Mail strokeWidth={1.5} size={20} className="text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Email</p>
                  <p className="text-white">hello@mindfulux.org</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <MapPin strokeWidth={1.5} size={20} className="text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Location</p>
                  <p className="text-white">San Francisco, CA</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center">
                  <Clock strokeWidth={1.5} size={20} className="text-neutral-400" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">Availability</p>
                  <p className="text-white">Mon - Fri, 9am - 6pm PST</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-neutral-800 focus:ring-1 focus:ring-white focus:border-white rounded-lg"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-neutral-900 border-neutral-800 focus:ring-1 focus:ring-white focus:border-white rounded-lg"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">
                  Company
                </label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="bg-neutral-900 border-neutral-800 focus:ring-1 focus:ring-white focus:border-white rounded-lg"
                  placeholder="Your company name"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <label className="block text-sm text-neutral-400 mb-2">
                  Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-neutral-900 border-neutral-800 focus:ring-1 focus:ring-white focus:border-white rounded-lg resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-neutral-200 rounded-full py-6 font-medium btn-shine"
                data-testid="contact-submit-btn"
              >
                {loading ? "Sending..." : "Send Message"}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 border-t border-neutral-900">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-2xl font-medium tracking-tight">
            <span className="text-white">Mindful</span>
            <span className="text-neutral-400">UX</span>
          </div>
          <p className="text-neutral-500 text-sm">
            © {new Date().getFullYear()} MindfulUX. Crafting Digital Experiences.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-neutral-500 hover:text-white transition-colors text-sm">
              Privacy
            </a>
            <a href="#" className="text-neutral-500 hover:text-white transition-colors text-sm">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <div className="bg-[#050505] min-h-screen">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#171717',
            border: '1px solid #262626',
            color: '#fff',
          },
        }}
      />
      <Navigation />
      <Hero />
      <About />
      <Services />
      <Portfolio />
      <CaseStudies />
      <Team />
      <Process />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
